import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, X } from "lucide-react";

/**
 * SearchableSelect — dropdown con ricerca a testo.
 * Props:
 *  - options: [{ value, label, subLabel? }]
 *  - value: string
 *  - onChange: (value) => void
 *  - placeholder: string
 *  - disabled: bool
 *  - required: bool
 *  - testId: string
 *  - className: string
 */
export default function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Seleziona...",
  disabled = false,
  required = false,
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() => options.find(o => o.value === value) || null, [options, value]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options.slice(0, 200);
    return options.filter(o => {
      const hay = `${o.label} ${o.subLabel || ""}`.toLowerCase();
      return hay.includes(s);
    }).slice(0, 200);
  }, [options, q]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const pick = (opt) => {
    onChange?.(opt.value);
    setOpen(false);
    setQ("");
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.("");
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      {/* Hidden input for HTML5 required validation */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden="true"
          required
          value={value}
          onChange={()=>{}}
          className="absolute opacity-0 h-0 w-0 pointer-events-none"
        />
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={()=>!disabled && setOpen(v => !v)}
        className={`gm-input w-full flex items-center justify-between text-left ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={`truncate ${!selected ? "text-zinc-500" : ""}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 ml-2 shrink-0">
          {selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e)=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); clear(e);} }}
              className="text-zinc-500 hover:text-white p-0.5 inline-flex"
              data-testid={testId ? `${testId}-clear` : undefined}
              aria-label="Pulisci selezione"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown size={14} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#0a0a0a] border border-zinc-700 rounded-sm shadow-2xl max-h-72 flex flex-col" data-testid={testId ? `${testId}-panel` : undefined}>
          <div className="relative border-b border-zinc-800 p-2">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" size={14} />
            <input
              ref={inputRef}
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Scrivi per cercare..."
              className="w-full bg-transparent pl-7 pr-2 py-1.5 text-sm outline-none border border-zinc-800 focus:border-[#4CAF50] rounded-sm"
              data-testid={testId ? `${testId}-search` : undefined}
              onKeyDown={(e)=>{
                if (e.key === "Enter" && filtered.length > 0) { e.preventDefault(); pick(filtered[0]); }
                if (e.key === "Escape") { setOpen(false); setQ(""); }
              }}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-zinc-500 text-center">Nessun risultato</div>
            )}
            {filtered.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={()=>pick(opt)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#4CAF50]/10 border-l-2 ${opt.value === value ? "border-[#4CAF50] bg-[#4CAF50]/5" : "border-transparent"}`}
                data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
              >
                <div className="truncate">{opt.label}</div>
                {opt.subLabel && <div className="text-[11px] text-zinc-500 truncate font-mono-data">{opt.subLabel}</div>}
              </button>
            ))}
          </div>
          {options.length > 200 && (
            <div className="px-3 py-1.5 text-[10px] text-zinc-600 border-t border-zinc-800 text-center">
              Mostrati max 200 su {options.length} — affina la ricerca
            </div>
          )}
        </div>
      )}
    </div>
  );
}
